import { useEffect, useRef } from "react";
import { Box } from "@mui/material";
import { Color, DotLegalChip, DotLegalDialog } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "@localization/useTranslation";
import MentionInputRender from "@common/components/mentionInput/MentionInputRender";
import RiskLevelChip from "../../RiskLevelChip";
import RiskTrajectory from "../../RiskTrajectory";
import RiskMitigationStatusChip from "../../RiskMitigationStatusChip";
import { RiskHistoryEventKind } from "../RiskHistoryOverview.types";
import { useRiskAssessmentTimelineDialog } from "./RiskAssessmentTimelineDialog.hooks";
import { useRiskAssessmentTimelineDialogStyles } from "./RiskAssessmentTimelineDialog.styles";
import { RiskAssessmentTimelineEntry } from "./RiskAssessmentTimelineDialog.types";

const ratingEventKinds = new Set<RiskHistoryEventKind>([
    RiskHistoryEventKind.Assessed,
    RiskHistoryEventKind.Reassessed,
    RiskHistoryEventKind.Reconfirmed,
    RiskHistoryEventKind.MitigationPromoted,
    RiskHistoryEventKind.ScaleMigrated,
    RiskHistoryEventKind.MatrixRebased,
]);

interface RiskAssessmentTimelineDialogProps {
    riskAssessmentId: string;
    entityName: string | null;
    scenarioName: string | null;
    // The event the user clicked to open the timeline, so its entry can be marked. The timeline is always
    // the full assessment history (newest first) — never cropped — so the clicked entry is not necessarily
    // the top one. Omitted when the dialog is opened without a specific originating event.
    highlightOccurredAt?: string | null;
    highlightKind?: RiskHistoryEventKind | null;
    onClose: () => void;
}

function RiskAssessmentTimelineDialog({
    riskAssessmentId,
    entityName,
    scenarioName,
    highlightOccurredAt,
    highlightKind,
    onClose,
}: RiskAssessmentTimelineDialogProps) {
    const styles = useRiskAssessmentTimelineDialogStyles();
    const { translateString, translateDateWithTime, translateDate } = useTranslation();
    const { timeline, isLoading, getEventLabel, getPlanStatusLabel, getConfidenceLabel, getRiskColor, getRiskIcon } =
        useRiskAssessmentTimelineDialog(riskAssessmentId);

    const subtitle = [entityName, scenarioName].filter((part): part is string => !!part).join(" · ");

    const isHighlighted = (entry: RiskAssessmentTimelineEntry) =>
        highlightKind != null &&
        !!highlightOccurredAt &&
        entry.kind === highlightKind &&
        new Date(entry.occurredAt).getTime() === new Date(highlightOccurredAt).getTime();

    // The timeline is the full history, so the clicked event can sit well below the fold; bring it into view
    // once the entries have rendered. Centred so the surrounding events stay visible for context. The scroll is
    // deferred past the dialog's enter transition — with cached data the entries render instantly, so an
    // immediate scroll would run while the dialog is still animating into place and be discarded.
    const highlightedRef = useRef<HTMLDivElement | null>(null);
    useEffect(() => {
        if (!timeline) return;
        const timeoutId = window.setTimeout(() => highlightedRef.current?.scrollIntoView({ block: "center" }), 300);
        return () => window.clearTimeout(timeoutId);
    }, [timeline]);

    const renderRiskChip = (level: number | null, label: string | null, scale: number | null) => {
        if (level == null || scale == null) return null;
        return <RiskLevelChip level={level} label={label ?? level.toString()} color={getRiskColor(level, scale)} icon={getRiskIcon(level, scale)} />;
    };

    // The risk movement a given event represents: a rating's before -> after, or a proposed plan's expected residual.
    const renderChange = (entry: RiskAssessmentTimelineEntry) => {
        if (ratingEventKinds.has(entry.kind) && entry.riskLevel != null) {
            const current = renderRiskChip(entry.riskLevel, entry.riskLevelLabel, entry.scale);
            return entry.previousRiskLevel != null ? (
                <RiskTrajectory from={renderRiskChip(entry.previousRiskLevel, entry.previousRiskLevelLabel, entry.previousScale)} to={current} />
            ) : (
                <RiskTrajectory from={current} />
            );
        }

        if (entry.kind === RiskHistoryEventKind.PlanProposed && entry.expectedRiskLevel != null) {
            return <RiskTrajectory from={renderRiskChip(entry.expectedRiskLevel, entry.expectedRiskLevelLabel, entry.expectedScale)} />;
        }

        return null;
    };

    const renderChipGroup = (names: Array<string>) =>
        names.length > 0 && (
            <Box sx={styles.chips}>
                {names.map((name) => (
                    <DotLegalChip key={name} value={name} isAutocomplete size="small" isSelected />
                ))}
            </Box>
        );

    const renderDimension = (label: string, level: number | null, levelLabel: string | null, chipNames: Array<string>, justification: string | null) => (
        <Box sx={styles.dimension}>
            <Box sx={styles.titleLevel}>
                <Box sx={styles.label}>{label}:</Box>
                <Box sx={styles.value}>{level != null ? `${level} – ${levelLabel ?? ""}` : "–"}</Box>
            </Box>
            {renderChipGroup(chipNames)}
            {!!justification && (
                <Box sx={styles.justification}>
                    <MentionInputRender value={justification} />
                </Box>
            )}
        </Box>
    );

    const renderRevisionBody = (entry: RiskAssessmentTimelineEntry) => (
        <>
            <Box sx={styles.dimensions}>
                {renderDimension(
                    translateString("consequenceLevel"),
                    entry.consequence,
                    entry.consequenceLabel,
                    entry.consequenceTypeNames,
                    entry.consequenceJustification
                )}
                {renderDimension(
                    translateString("probabilityLevel"),
                    entry.probability,
                    entry.probabilityLabel,
                    entry.measureNames,
                    entry.probabilityJustification
                )}
            </Box>
            {entry.confidence != null && (
                <Box sx={styles.detailRow}>
                    <Box sx={styles.label}>{translateString("assessmentConfidence")}:</Box>
                    <Box sx={styles.value}>{getConfidenceLabel(entry.confidence)}</Box>
                </Box>
            )}
            {entry.participantNames.length > 0 && (
                <Box sx={styles.detailRow}>
                    <Box sx={styles.label}>{translateString("participants")}:</Box>
                    <Box sx={styles.value}>{entry.participantNames.join(", ")}</Box>
                </Box>
            )}
            {!!entry.reviewNote && (
                <Box sx={styles.detailRow}>
                    <Box sx={styles.label}>{translateString("reviewNoteLabel")}:</Box>
                    <Box sx={styles.reason}>{entry.reviewNote}</Box>
                </Box>
            )}
        </>
    );

    const renderPlanBody = (entry: RiskAssessmentTimelineEntry) => (
        <Box sx={styles.detailGrid}>
            {!!entry.description && <MentionInputRender value={entry.description} />}
            {renderChipGroup(entry.plannedMeasureNames)}
            {!!entry.responsibleName && (
                <Box sx={styles.detailRow}>
                    <Box sx={styles.label}>{translateString("responsible")}:</Box>
                    <Box sx={styles.value}>{entry.responsibleName}</Box>
                </Box>
            )}
            {!!entry.deadline && (
                <Box sx={styles.detailRow}>
                    <Box sx={styles.label}>{translateString("deadline")}:</Box>
                    <Box sx={styles.value}>{translateDate(new Date(entry.deadline))}</Box>
                </Box>
            )}
        </Box>
    );

    const renderReasonBody = (entry: RiskAssessmentTimelineEntry) => (
        <Box sx={styles.detailGrid}>
            {!!entry.reason && <Box sx={styles.reason}>{entry.reason}</Box>}
            {!!entry.reviewBy && (
                <Box sx={styles.detailRow}>
                    <Box sx={styles.label}>{translateString("riskMitigationAcceptReviewBy")}:</Box>
                    <Box sx={styles.value}>{translateDate(new Date(entry.reviewBy))}</Box>
                </Box>
            )}
        </Box>
    );

    const renderBody = (entry: RiskAssessmentTimelineEntry) => {
        if (ratingEventKinds.has(entry.kind)) return renderRevisionBody(entry);
        if (entry.kind === RiskHistoryEventKind.PlanProposed) return renderPlanBody(entry);
        if (
            entry.kind === RiskHistoryEventKind.RiskAccepted ||
            entry.kind === RiskHistoryEventKind.RiskAvoided ||
            entry.kind === RiskHistoryEventKind.Retired ||
            entry.kind === RiskHistoryEventKind.Reinstated
        )
            return renderReasonBody(entry);
        return null;
    };

    const renderEntry = (entry: RiskAssessmentTimelineEntry, index: number, total: number) => {
        const change = renderChange(entry);
        const body = renderBody(entry);
        const highlighted = isHighlighted(entry);
        return (
            <Box sx={styles.entry} key={`${entry.kind}-${entry.occurredAt}-${index}`} ref={highlighted ? highlightedRef : undefined}>
                <Box sx={styles.rail}>
                    <Box sx={[styles.dot, ...(entry.isPromoteMoment ? [styles.dotPromote] : []), ...(highlighted ? [styles.dotHighlight] : [])]} />
                    {index < total - 1 && <Box sx={[styles.line, ...(highlighted ? [styles.lineHighlight] : [])]} />}
                </Box>
                <Box sx={styles.content}>
                    <Box sx={styles.header}>
                        <Box sx={styles.eventLabel}>{getEventLabel(entry.kind)}</Box>
                        {entry.planStatus != null && <RiskMitigationStatusChip mitigationPlanStatus={entry.planStatus} />}
                        {entry.isDrifted && <DotLegalChip value={translateString("riskTimelineDrifted")} color={Color.gold} size="small" />}
                    </Box>
                    <Box sx={styles.meta}>
                        {translateDateWithTime(new Date(entry.occurredAt))}
                        {entry.actorName ? ` · ${entry.actorName}` : ""}
                    </Box>
                    {change}
                    {body}
                </Box>
            </Box>
        );
    };

    const entries = timeline?.entries ?? [];

    return (
        <DotLegalDialog
            open
            size="lg"
            isLoading={isLoading}
            header={
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                    {translateString("riskTimelineTitle")}
                    {!!subtitle && <Box sx={styles.subtitle}>{subtitle}</Box>}
                </Box>
            }
            buttonOkText={translateString("close")}
            onOkClick={onClose}
            onDialogClose={onClose}
        >
            {entries.length === 0 ? (
                <Box sx={styles.reason}>{translateString("noData")}</Box>
            ) : (
                <Box sx={styles.timeline}>{entries.map((entry, index) => renderEntry(entry, index, entries.length))}</Box>
            )}
        </DotLegalDialog>
    );
}

export default RiskAssessmentTimelineDialog;
